import { useEffect, useState } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import {
	fetchLinkedJiraWorkItemUrls,
	fetchEmptyLinkedJiraAggregate,
	isLinkedJiraWorkItemsClientError,
} from '../utils/fetchLinkedJiraWorkItemUrls.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import type { RemoteLinksTimings } from './incomingLinksAnalytics.ts';
import { createRemoteLinksObservation } from './remoteLinksObservation.ts';
import { useDependencyHealthAnalytics } from './useDependencyHealthAnalytics.ts';

// Bounds the best-effort Jira remote-link fallback so a stuck app-link call is reported as a timeout
// instead of merging minutes later. The aggregate gate gets its own deadline of the same length.
const REMOTE_LINKS_TIMEOUT_MS = 5000;

export type RemoteLinksResult =
	| {
			status: 'resolved';
			fetched: number;
			links: EnhancedSmartLink[];
			timings?: RemoteLinksTimings;
	  }
	| { status: 'skipped' }
	| { status: 'failed' };

export type UseIncomingLinksRemoteLinksOptions = {
	skip: boolean;
	hydrateLinks: (urls: string[]) => Promise<EnhancedSmartLink[]>;
};

export const useIncomingLinksRemoteLinks = (
	contentId: string | undefined,
	{ skip, hydrateLinks }: UseIncomingLinksRemoteLinksOptions,
): RemoteLinksResult | null => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const reportHealth = useDependencyHealthAnalytics();
	const [remoteLinksResult, setRemoteLinksResult] = useState<RemoteLinksResult | null>(null);

	useEffect(() => {
		if (skip || !contentId) {
			return;
		}

		let cancelled = false;
		let timedOut = false;
		let aggregateTimedOut = false;
		const observation = createRemoteLinksObservation(createEvent, reportHealth);
		let timer: ReturnType<typeof setTimeout> | undefined;
		const controller = new AbortController();
		const aggregateController = new AbortController();
		const aggregateTimer = setTimeout(() => {
			aggregateTimedOut = true;
			aggregateController.abort();
		}, REMOTE_LINKS_TIMEOUT_MS);

		const fetchRemoteLinks = () => {
			timer = setTimeout(() => {
				timedOut = true;
				controller.abort();
			}, REMOTE_LINKS_TIMEOUT_MS);

			observation.requestStarted();
			fetchLinkedJiraWorkItemUrls(contentId, controller.signal)
				.then(async (urls) => {
					// The deadline covers the REST call only; hydration has ORS's own failure handling.
					clearTimeout(timer);
					observation.requestFinished();
					observation.hydrationStarted(urls.length);
					const links = urls.length > 0 ? await hydrateLinks(urls) : [];
					observation.hydrationFinished();
					return { fetched: urls.length, links };
				})
				.then(({ fetched, links: hydrated }) => {
					if (!cancelled) {
						setRemoteLinksResult({
							status: 'resolved',
							fetched,
							links: hydrated,
							timings: observation.timings(),
						});
					}
				})
				.catch((err: unknown) => {
					if (cancelled) {
						return;
					}
					// 4xx is expected for anonymous viewers and sites without a Jira app link, and a
					// timeout is our own deadline; neither is worth a monitoring report.
					if (!isLinkedJiraWorkItemsClientError(err) && !timedOut) {
						const error = err instanceof Error ? err : new Error(String(err));
						getMonitoringClient().submitError(error, { attribution: Attribution.INTEGRATIONS });
					}
					observation.failed(err, timedOut);
					setRemoteLinksResult({ status: 'failed' });
				});
		};

		// Never rejects: any failure resolves null and falls through to the remote-link request.
		void fetchEmptyLinkedJiraAggregate(contentId, aggregateController.signal, (result) =>
			observation.aggregateSettled(result, aggregateTimedOut),
		).then((empty) => {
			clearTimeout(aggregateTimer);
			if (cancelled) {
				return;
			}
			if (!empty) {
				fetchRemoteLinks();
				return;
			}
			observation.skipped(empty.incomplete);
			setRemoteLinksResult({ status: 'skipped' });
		});

		return () => {
			cancelled = true;
			observation.cancel();
			clearTimeout(aggregateTimer);
			clearTimeout(timer);
			aggregateController.abort();
			controller.abort();
		};
	}, [skip, contentId, hydrateLinks, createEvent, reportHealth]);

	return remoteLinksResult;
};
